/**
 * SPATIALx Media — The Downconverter
 * ----------------------------------
 * The store's signature interactive. A point-cloud of spatial light (left)
 * that the visitor scrubs down into the flat artifact they can actually buy
 * today (right): a mirror-symmetric waveform for albums, a two-page spread
 * wireframe for episodes. For albums the scrub is also the audio preview.
 *
 * Canvas 2D only. Zero dependencies. IIFE, loaded with `defer`.
 * Initialised per-element via IntersectionObserver — never on page load.
 * Public API: window.SXDownconverter.init(el)
 *
 * DOM contract: see CONVENTIONS.md — .sx-downconverter wrapper carrying
 * data-mode / data-beam-a / data-beam-b / data-catalogue / data-spatial-status
 * / data-tracks, containing <canvas>, input.sx-dc-scrub (0–1000, 1000 = flat),
 * .sx-dc-labels and ol.sx-dc-tracklist with button[data-track-index].
 */
(function () {
  'use strict';

  var COUNT = 1000;        // particles in the cloud
  var SPRITES = 12;        // pre-rendered glow sprites along the beam gradient
  var STAGGER = 0.3;       // per-particle resolve stagger (fraction of scrub)
  var KEY_STEP = 20;       // ArrowLeft / ArrowRight scrub step
  var reducedMQ = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  /* ------------------------------------------------------------ utils --- */

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  /* Eased, cubic (ease-in-out) interpolation for the scrub. */
  function easeCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function hexRgb(hex, fallback) {
    var m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) { return fallback.slice(); }
    var n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function mixRgb(a, b, t) {
    return [
      Math.round(a[0] + (b[0] - a[0]) * t),
      Math.round(a[1] + (b[1] - a[1]) * t),
      Math.round(a[2] + (b[2] - a[2]) * t)
    ];
  }

  function rgba(c, a) {
    return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a + ')';
  }

  /* Point at fraction t (0–1) around a rectangle's perimeter, clockwise. */
  function rectPoint(x, y, w, h, t) {
    var per = 2 * (w + h);
    var d = (t % 1) * per;
    if (d < w) { return [x + d, y]; }
    d -= w;
    if (d < h) { return [x + w, y + d]; }
    d -= h;
    if (d < w) { return [x + w - d, y + h]; }
    d -= w;
    return [x, y + h - d];
  }

  /* Fallback peaks when a track carries none — quiet, plausible, symmetric. */
  function synthPeaks() {
    var p = [];
    for (var i = 0; i < 64; i++) {
      p.push(clamp(0.18 + 0.7 * Math.abs(
        Math.sin(i * 0.37) + 0.35 * Math.sin(i * 1.71)) / 1.35, 0.05, 1));
    }
    return p;
  }

  /* --------------------------------------------------------- instance --- */

  function Downconverter(el) {
    this.el = el;
    this.canvas = el.querySelector('canvas');
    this.scrub = el.querySelector('.sx-dc-scrub');
    this.nowEl = el.querySelector('.sx-dc-now');
    this.buttons = el.querySelectorAll('button[data-track-index]');
    this.rows = el.querySelectorAll('li[data-row-index]');
    this.toggleBtn = el.querySelector('.sx-dc-toggle');
    this.prevBtn = el.querySelector('.sx-dc-prev');
    this.nextBtn = el.querySelector('.sx-dc-next');
    this.timeCur = el.querySelector('.sx-dc-time-cur');
    this.timeDur = el.querySelector('.sx-dc-time-dur');
    this.visBtns = el.querySelectorAll('.sx-dc-vis button');
    if (!this.canvas || !this.scrub) { return; }

    this.ctx = this.canvas.getContext('2d');
    this.mode = el.getAttribute('data-mode') === 'spread' ? 'spread' : 'waveform';
    this.beamA = hexRgb(el.getAttribute('data-beam-a'), [193, 59, 255]);
    this.beamB = hexRgb(el.getAttribute('data-beam-b'), [255, 77, 109]);
    this.catalogue = (el.getAttribute('data-catalogue') || '').toUpperCase();
    this.status = (el.getAttribute('data-spatial-status') || '').toUpperCase();
    this.reduced = (reducedMQ && reducedMQ.matches) || el.hasAttribute('data-reduced');

    var dim = '';
    try { dim = getComputedStyle(el).getPropertyValue('--dim').trim(); } catch (e) {}
    this.dim = dim || '#8A8A96';

    try { this.tracks = JSON.parse(el.getAttribute('data-tracks') || '[]'); }
    catch (e) { this.tracks = []; }
    if (!Array.isArray(this.tracks)) { this.tracks = []; }

    /* Every release now runs the oscilloscope — the switcher has been
       retired; the internal engine still supports waveform/orbit/scope
       for future use. */
    this.vis = 'scope';

    this.trackIndex = 0;
    this.audio = null;          // HTMLAudioElement
    this.actx = null;           // AudioContext (waveform mode, full motion)
    this.analyser = null;
    this.timeData = null;
    this.playing = false;
    this.pointerDown = false;
    this.amp = 0;               // smoothed analyser amplitude, 0–1
    this.visible = false;
    this.running = false;
    this.raf = 0;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = 0;
    this.h = 0;
    this.aborter = ('AbortController' in window) ? new AbortController() : null;

    this.buildSprites();
    this.buildParticles();
    this.bind();
    this.measure();
    this.setAria();
    this.syncVisButtons();

    var self = this;
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(function () { self.measure(); self.wake(); });
      this.ro.observe(el);
    }
    this.io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        self.visible = entries[i].isIntersecting;
      }
      if (self.visible) { self.wake(); } else { self.sleep(); }
    }, { rootMargin: '80px' });
    this.io.observe(el);
  }

  /* Pre-render soft-glow radial sprites along beam-a → beam-b (perf: no
     per-particle shadowBlur in the hot loop). */
  Downconverter.prototype.buildSprites = function () {
    this.sprites = [];
    var S = 32, r = S / 2;
    for (var k = 0; k < SPRITES; k++) {
      var t = SPRITES > 1 ? k / (SPRITES - 1) : 0;
      var c = mixRgb(this.beamA, this.beamB, t);
      var cv = document.createElement('canvas');
      cv.width = S; cv.height = S;
      var g = cv.getContext('2d');
      var grad = g.createRadialGradient(r, r, 0, r, r, r);
      grad.addColorStop(0, rgba(c, 0.95));
      grad.addColorStop(0.35, rgba(c, 0.4));
      grad.addColorStop(1, rgba(c, 0));
      g.fillStyle = grad;
      g.fillRect(0, 0, S, S);
      this.sprites.push(cv);
    }
  };

  /* Particles: fixed per-particle randoms so resize/track-change recomputes
     targets deterministically. Colour index follows cloud x (beam along x). */
  Downconverter.prototype.buildParticles = function () {
    this.parts = [];
    for (var i = 0; i < COUNT; i++) {
      var ang = Math.random() * Math.PI * 2;
      var rad = Math.sqrt(Math.random());
      var u = Math.cos(ang) * rad;
      var v = Math.sin(ang) * rad * 0.82;
      this.parts.push({
        u: u, v: v,
        ci: clamp(Math.round(((u + 1) / 2) * (SPRITES - 1)), 0, SPRITES - 1),
        s: 0.6 + Math.random() * 1.2,
        ph1: Math.random() * Math.PI * 2,
        ph2: Math.random() * Math.PI * 2,
        sp: 0.25 + Math.random() * 0.5,
        o: Math.random() * STAGGER,
        a: Math.random(), b: Math.random(),
        c: Math.random(), d: Math.random(),
        tx: 0, ty: 0
      });
    }
  };

  Downconverter.prototype.measure = function () {
    var rect = this.canvas.getBoundingClientRect();
    this.w = Math.max(1, rect.width);
    this.h = Math.max(1, rect.height || this.w * 0.56);
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(this.w * this.dpr);
    this.canvas.height = Math.round(this.h * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.cloudCX = this.w * 0.28;
    this.cloudCY = this.h * 0.52;
    this.cloudR = Math.min(this.w * 0.2, this.h * 0.36);
    this.buildNebula();
    this.computeTargets();
  };

  /* Soft nebula bloom behind the unresolved cloud (cached per measure). */
  Downconverter.prototype.buildNebula = function () {
    var r = this.cloudR * 2.1;
    var core = mixRgb(this.beamA, this.beamB, 0.35);
    var g = this.ctx.createRadialGradient(
      this.cloudCX, this.cloudCY, 0, this.cloudCX, this.cloudCY, r);
    g.addColorStop(0, rgba(core, 0.16));
    g.addColorStop(0.55, rgba(this.beamA, 0.07));
    g.addColorStop(1, rgba(this.beamA, 0));
    this.nebula = g;
    this.nebulaR = r;
  };

  Downconverter.prototype.currentPeaks = function () {
    var t = this.tracks[this.trackIndex];
    var p = t && Array.isArray(t.peaks) && t.peaks.length ? t.peaks : null;
    return p || synthPeaks();
  };

  Downconverter.prototype.computeTargets = function () {
    if (this.mode === 'spread') { this.spreadTargets(); }
    else if (this.vis === 'orbit') { this.orbitTargets(); }
    else if (this.vis === 'scope') { this.scopeTargets(); }
    else { this.waveTargets(); }
  };

  Downconverter.prototype.setVis = function (mode) {
    if (mode !== 'waveform' && mode !== 'orbit' && mode !== 'scope') { return; }
    if (this.mode === 'spread' || mode === this.vis) { return; }
    this.vis = mode;
    try { window.localStorage.setItem(this.visKey, mode); } catch (e) {}
    this.computeTargets();
    this.syncVisButtons();
    this.wake();
  };

  Downconverter.prototype.syncVisButtons = function () {
    if (!this.visBtns) { return; }
    for (var i = 0; i < this.visBtns.length; i++) {
      var on = this.visBtns[i].getAttribute('data-vis') === this.vis;
      this.visBtns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      this.visBtns[i].classList.toggle('sx-dc-vis--on', on);
    }
  };

  Downconverter.prototype.orbitGeom = function () {
    return {
      cx: this.w * 0.56,
      cy: this.h * 0.52,
      r0: Math.min(this.w * 0.13, this.h * 0.30),
      r1: Math.min(this.w * 0.10, this.h * 0.22)
    };
  };

  /* Flat target C — the peaks wrapped into an orbital ring. */
  Downconverter.prototype.orbitTargets = function () {
    var peaks = this.currentPeaks();
    var n = Math.min(peaks.length, 96);
    var g = this.orbitGeom();
    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i];
      var bar = Math.min(n - 1, Math.floor(p.a * n));
      var amp = 0.08 + 0.92 * clamp(+peaks[bar] || 0, 0, 1);
      var ang = ((bar + p.b * 0.9) / n) * Math.PI * 2 - Math.PI / 2;
      var rr = g.r0 + p.c * amp * g.r1;
      p.tx = g.cx + Math.cos(ang) * rr;
      p.ty = g.cy + Math.sin(ang) * rr;
    }
  };

  /* Flat target D — a tight oscilloscope band. */
  Downconverter.prototype.scopeTargets = function () {
    var peaks = this.currentPeaks();
    var n = Math.min(peaks.length, 96);
    var x0 = this.w * 0.08;
    var innerW = this.w * 0.84;
    var cy = this.h * 0.52;
    var maxH = this.h * 0.26;
    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i];
      var bar = Math.min(n - 1, Math.floor(p.a * n));
      var amp = clamp(+peaks[bar] || 0, 0, 1);
      p.tx = x0 + p.a * innerW;
      p.ty = cy + (p.c * 2 - 1) * (0.06 + amp * 0.30) * maxH;
    }
  };

  /* Flat target A — mirror-symmetric bar waveform from the current track. */
  Downconverter.prototype.waveTargets = function () {
    var peaks = this.currentPeaks();
    var nBars = Math.min(peaks.length, 96);
    var x0 = this.w * 0.08;
    var innerW = this.w * 0.84;
    var step = innerW / nBars;
    var cy = this.h * 0.52;
    var maxH = this.h * 0.3;
    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i];
      var bar = Math.min(nBars - 1, Math.floor(p.a * nBars));
      var amp = 0.06 + 0.94 * clamp(+peaks[bar] || 0, 0, 1);
      p.tx = x0 + (bar + 0.5) * step + (p.b - 0.5) * step * 0.7;
      p.ty = cy + (p.c * 2 - 1) * amp * maxH; /* c uniform → mirror around cy */
    }
  };

  /* Flat target B — a two-page book spread: page outlines, spine, text rows. */
  Downconverter.prototype.spreadTargets = function () {
    var SW = this.w * 0.66;
    var PH = Math.min(this.h * 0.56, SW * 0.42);
    var sx = this.w * 0.5 - SW / 2;
    var sy = this.h * 0.52 - PH / 2;
    var gap = Math.max(6, SW * 0.015);
    var pw = (SW - gap) / 2;
    var nLines = 6;
    var inset = pw * 0.12;
    var lineGap = (PH - inset * 2.2) / (nLines - 1);
    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i];
      var page = p.b < 0.5 ? 0 : 1;
      var px = page === 0 ? sx : sx + pw + gap;
      var pt;
      if (p.a < 0.4) {                       /* page outlines */
        pt = rectPoint(px, sy, pw, PH, p.c);
        p.tx = pt[0]; p.ty = pt[1];
      } else if (p.a < 0.48) {               /* spine */
        p.tx = sx + pw + gap / 2 + (p.d - 0.5) * 1.5;
        p.ty = sy + p.c * PH;
      } else {                               /* text-line rows */
        var ln = Math.min(nLines - 1, Math.floor(p.c * nLines));
        var len = (pw - inset * 2) *
          (ln === nLines - 1 ? 0.55 : 0.82 + 0.18 * Math.sin(ln * 2.7 + page * 1.3));
        p.tx = px + inset + p.d * len;
        p.ty = sy + inset * 1.1 + ln * lineGap;
      }
    }
  };

  /* ------------------------------------------------------------ audio --- */

  Downconverter.prototype.audioEnabled = function () {
    return this.mode === 'waveform' && this.tracks.length > 0;
  };

  Downconverter.prototype.watchPlayback = function (resume) {
    var self = this;
    if (!this.el.isConnected || (this.aborter && this.aborter.signal.aborted)) { return false; }
    var listeningPass = window.SpatialxListeningPass;
    if (listeningPass && listeningPass.watchMedia && this.audio) listeningPass.watchMedia(this.audio, {
      source: 'downconverter',
      trigger: document.activeElement,
      resume: function () {
        if (self.el.isConnected && !(self.aborter && self.aborter.signal.aborted)) { resume(); }
      }
    });
    return true;
  };

  Downconverter.prototype.ensureAudio = function () {
    if (!this.audioEnabled() || this.audio) { return; }
    var self = this;
    var a = new Audio();
    a.crossOrigin = 'anonymous';
    a.preload = 'metadata';
    var t = this.tracks[this.trackIndex];
    if (t && t.previewUrl) { a.src = t.previewUrl; }
    a.addEventListener('play', function () {
      self.playing = true; self.syncButtons(); self.wake();
      // Site-wide audio exclusivity: announce this player starting so the
      // radio bar and card previews (spatialx-radio.js / spatialx-card-
      // actions.js) pause themselves. Every play path — transport toggle,
      // tracklist rows, scrub-to-play, auto-advance — funnels through this
      // 'play' event, so this is the one choke point.
      try {
        window.dispatchEvent(new CustomEvent('sx:audio-play', { detail: { source: 'downconverter', owner: self.el } }));
      } catch (e) {}
    });
    a.addEventListener('pause', function () { self.playing = false; self.syncButtons(); self.wake(); });
    /* End of a preview rolls into the next track — the album plays through. */
    a.addEventListener('ended', function () {
      self.playing = false;
      if (self.trackIndex < self.tracks.length - 1) {
        self.pressTrack(self.trackIndex + 1);
      } else {
        self.updateTime();
        self.syncButtons();
        self.wake();
      }
    });
    a.addEventListener('timeupdate', function () { self.updateTime(); });
    a.addEventListener('loadedmetadata', function () { self.updateTime(); });
    this.audio = a;
    /* First pointer interaction creates the AudioContext (full-motion only —
       reduced mode keeps plain HTMLAudioElement play/pause). */
    if (!this.reduced) {
      try {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          this.actx = new AC();
          var src = this.actx.createMediaElementSource(a);
          this.analyser = this.actx.createAnalyser();
          this.analyser.fftSize = 512;
          this.timeData = new Uint8Array(this.analyser.fftSize);
          src.connect(this.analyser);
          this.analyser.connect(this.actx.destination);
        }
      } catch (e) { this.actx = null; this.analyser = null; }
    }
  };

  Downconverter.prototype.seekFrac = function (frac) {
    if (!this.audio) { return; }
    var self = this;
    if (!this.watchPlayback(function () { self.seekFrac(frac); })) { return; }
    var apply = function () {
      if (isFinite(self.audio.duration) && self.audio.duration > 0) {
        try { self.audio.currentTime = frac * self.audio.duration; } catch (e) {}
      }
    };
    if (isFinite(this.audio.duration) && this.audio.duration > 0) { apply(); }
    else { this.audio.addEventListener('loadedmetadata', apply, { once: true }); }
    if (this.actx && this.actx.state === 'suspended') { this.actx.resume(); }
    var pr = this.audio.play();
    if (pr && pr.catch) { pr.catch(function () {}); }
  };

  Downconverter.prototype.pressTrack = function (i) {
    if (!this.audioEnabled()) { return; }
    if (this.audio && i === this.trackIndex && !this.audio.paused) {
      this.audio.pause();
      return;
    }
    var self = this;
    this.ensureAudio();
    if (!this.audio) { return; }
    if (i !== this.trackIndex) {
      this.trackIndex = i;
      var t = this.tracks[i];
      if (t && t.previewUrl) { this.audio.src = t.previewUrl; }
      if (this.mode === 'waveform') { this.computeTargets(); }
      this.updateTime();
    }
    if (!this.watchPlayback(function () { self.pressTrack(i); })) { return; }
    if (this.actx && this.actx.state === 'suspended') { this.actx.resume(); }
    var pr = this.audio.play();
    if (pr && pr.catch) { pr.catch(function () {}); }
    this.syncButtons();
    this.wake();
  };

  Downconverter.prototype.fmtTime = function (s) {
    if (!isFinite(s) || s < 0) { s = 0; }
    var m = Math.floor(s / 60);
    var r = Math.floor(s % 60);
    return m + ':' + (r < 10 ? '0' : '') + r;
  };

  Downconverter.prototype.updateTime = function () {
    if (!this.audio) { return; }
    var cur = this.audio.currentTime || 0;
    var dur = isFinite(this.audio.duration) ? this.audio.duration : 0;
    if (this.timeCur) { this.timeCur.textContent = this.fmtTime(cur); }
    if (this.timeDur) { this.timeDur.textContent = this.fmtTime(dur); }
    var row = this.rows && this.rows[this.trackIndex];
    if (row) {
      var frac = dur > 0 ? clamp(cur / dur, 0, 1) : 0;
      row.style.setProperty('--sx-prog', frac.toFixed(4));
    }
  };

  Downconverter.prototype.syncButtons = function () {
    var t = this.tracks[this.trackIndex];
    var started = !!(this.audio && this.audio.currentTime > 0);
    if (this.nowEl) {
      if (t && (this.playing || started)) {
        this.nowEl.textContent =
          (this.playing ? 'NOW RECEIVING — ' : 'SIGNAL HELD — ') + (t.title || '');
      } else {
        this.nowEl.textContent = 'PREVIEW CHANNEL — SELECT A TRACK';
      }
    }
    if (this.toggleBtn) {
      this.toggleBtn.innerHTML = this.playing ? '&#10074;&#10074;' : '&#9654;';
      this.toggleBtn.setAttribute('aria-label',
        this.playing ? 'Pause preview' : 'Play preview');
    }
    for (var i = 0; i < this.buttons.length; i++) {
      var idx = parseInt(this.buttons[i].getAttribute('data-track-index'), 10);
      var on = idx === this.trackIndex && this.playing;
      this.buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      this.buttons[i].innerHTML = on ? '&#10074;&#10074;' : '&#9654;';
    }
    for (var j = 0; j < this.rows.length; j++) {
      var row = this.rows[j];
      var ri = parseInt(row.getAttribute('data-row-index'), 10);
      var isCurrent = ri === this.trackIndex;
      row.classList.toggle('sx-dc-track--active', isCurrent && (this.playing || started));
      row.classList.toggle('sx-dc-track--playing', isCurrent && this.playing);
      if (!isCurrent) { row.style.removeProperty('--sx-prog'); }
    }
  };

  /* ----------------------------------------------------------- events --- */

  Downconverter.prototype.bind = function () {
    var self = this;
    var opts = this.aborter ? { signal: this.aborter.signal } : false;

    /* First pointer interaction anywhere in the module arms the audio. */
    this.el.addEventListener('pointerdown', function () {
      self.ensureAudio();
    }, opts);

    /* Mirror of the 'sx:audio-play' dispatch in ensureAudio(): when any
       OTHER audio surface starts (radio bar, card preview, another
       downconverter instance), pause this one. The abort signal ties the
       listener's lifetime to destroy(). */
    window.addEventListener('sx:audio-play', function (e) {
      if (e.detail && e.detail.owner === self.el) { return; }
      if (self.audio && !self.audio.paused) { self.audio.pause(); }
    }, opts);

    this.scrub.addEventListener('pointerdown', function () {
      self.pointerDown = true;
    }, opts);
    window.addEventListener('pointerup', function () {
      /* Releasing keeps the audio playing — only the drag flag clears. */
      self.pointerDown = false;
    }, opts);

    this.scrub.addEventListener('input', function () {
      self.setAria();
      if (self.pointerDown && self.audioEnabled()) {
        self.ensureAudio();
        self.seekFrac(self.value() / 1000);
      }
      self.wake();
    }, opts);

    this.scrub.addEventListener('keydown', function (e) {
      var v = self.value();
      var next = null;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { next = v - KEY_STEP; }
      else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { next = v + KEY_STEP; }
      else if (e.key === 'Home') { next = 0; }
      else if (e.key === 'End') { next = 1000; }
      if (next !== null) {
        e.preventDefault();
        self.scrub.value = clamp(next, 0, 1000);
        self.setAria();
        self.wake();
      }
    }, opts);

    for (var i = 0; i < this.buttons.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var idx = parseInt(btn.getAttribute('data-track-index'), 10);
          if (!isNaN(idx) && self.tracks[idx]) { self.pressTrack(idx); }
        }, opts);
      })(this.buttons[i]);
    }

    /* The whole tracklist row is a target — the small ▶ is just its focus stop. */
    var list = this.el.querySelector('.sx-dc-tracklist');
    if (list) {
      list.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('button')) { return; }
        var li = e.target.closest ? e.target.closest('li[data-row-index]') : null;
        if (!li) { return; }
        var idx = parseInt(li.getAttribute('data-row-index'), 10);
        if (!isNaN(idx) && self.tracks[idx]) { self.pressTrack(idx); }
      }, opts);
    }

    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', function () {
        self.pressTrack(self.trackIndex || 0);
      }, opts);
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        if (!self.audioEnabled()) { return; }
        self.ensureAudio();
        /* More than 3s in: restart the track. Otherwise: previous track. */
        if (self.audio && self.audio.currentTime > 3) {
          try { self.audio.currentTime = 0; } catch (e) {}
          self.updateTime();
        } else if (self.trackIndex > 0) {
          self.pressTrack(self.trackIndex - 1);
        } else {
          self.pressTrack(0);
        }
      }, opts);
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        if (!self.audioEnabled()) { return; }
        self.pressTrack((self.trackIndex + 1) % self.tracks.length);
      }, opts);
    }

    for (var vb = 0; vb < this.visBtns.length; vb++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          self.setVis(btn.getAttribute('data-vis'));
        }, opts);
      })(this.visBtns[vb]);
    }

    if (reducedMQ && reducedMQ.addEventListener) {
      reducedMQ.addEventListener('change', function (e) {
        self.reduced = e.matches || self.el.hasAttribute('data-reduced');
        if (self.reduced) { self.sleep(); }
        self.wake();
      }, opts);
    }
  };

  Downconverter.prototype.value = function () {
    return clamp(parseInt(this.scrub.value, 10) || 0, 0, 1000);
  };

  Downconverter.prototype.setAria = function () {
    this.scrub.setAttribute('aria-valuetext',
      Math.round(this.value() / 10) + '% flat');
  };

  /* ------------------------------------------------------- render loop --- */

  Downconverter.prototype.needsLoop = function () {
    return this.visible && !this.reduced && (this.value() < 1000 || this.playing);
  };

  /* Draw once now; keep the loop alive only while there is motion to show. */
  Downconverter.prototype.wake = function () {
    if (!this.visible) { return; }
    if (this.reduced) { this.drawStatic(); return; }
    if (this.running) { return; }
    var self = this;
    this.running = true;
    var tick = function () {
      if (!self.running) { return; }
      self.draw(performance.now() / 1000);
      if (self.needsLoop()) { self.raf = requestAnimationFrame(tick); }
      else { self.running = false; }
    };
    this.raf = requestAnimationFrame(tick);
  };

  Downconverter.prototype.sleep = function () {
    this.running = false;
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
  };

  Downconverter.prototype.readAmp = function () {
    var target = 0;
    if (this.playing && this.analyser && this.timeData) {
      this.analyser.getByteTimeDomainData(this.timeData);
      var sum = 0;
      for (var i = 0; i < this.timeData.length; i += 4) {
        var d = (this.timeData[i] - 128) / 128;
        sum += d * d;
      }
      target = clamp(Math.sqrt(sum / (this.timeData.length / 4)) * 2.4, 0, 1);
    }
    /* Clamp per-frame change — subtle pulse, never a strobe. */
    this.amp += clamp(target - this.amp, -0.045, 0.045);
  };

  /* The flat artifact itself: a mirrored, gradient-filled waveform that
     assembles as the scrub resolves, breathes with the analyser amplitude,
     and carries a sweeping playhead while a preview is audible. */
  Downconverter.prototype.drawWave = function (alpha, T) {
    if (this.mode !== 'waveform' || alpha <= 0.01) { return; }
    var ctx = this.ctx;
    var peaks = this.currentPeaks();
    var n = Math.min(peaks.length, 96);
    var x0 = this.w * 0.08;
    var innerW = this.w * 0.84;
    var step = innerW / n;
    var cy = this.h * 0.52;
    var maxH = this.h * 0.3;
    var pulse = 1 + this.amp * 0.22;
    var i, a, x;

    var path = null;
    if (typeof Path2D !== 'undefined') {
      path = new Path2D();
    } else {
      ctx.beginPath();
    }
    var P = path || ctx;
    P.moveTo(x0, cy);
    for (i = 0; i < n; i++) {
      a = (0.06 + 0.94 * clamp(+peaks[i] || 0, 0, 1)) * maxH * pulse;
      x = x0 + (i + 0.5) * step;
      P.lineTo(x, cy - a);
    }
    P.lineTo(x0 + innerW, cy);
    for (i = n - 1; i >= 0; i--) {
      a = (0.06 + 0.94 * clamp(+peaks[i] || 0, 0, 1)) * maxH * pulse;
      x = x0 + (i + 0.5) * step;
      P.lineTo(x, cy + a);
    }
    P.closePath();

    var grad = ctx.createLinearGradient(x0, 0, x0 + innerW, 0);
    grad.addColorStop(0, rgba(this.beamA, 0.30));
    grad.addColorStop(1, rgba(this.beamB, 0.30));

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = grad;
    if (path) { ctx.fill(path); } else { ctx.fill(); }

    /* Playhead: everything already heard burns brighter, and a thin line
       marks the read position. */
    if (path && this.audio && this.playing &&
        isFinite(this.audio.duration) && this.audio.duration > 0) {
      var frac = clamp(this.audio.currentTime / this.audio.duration, 0, 1);
      ctx.save();
      ctx.beginPath();
      ctx.rect(x0, cy - maxH * 1.25, innerW * frac, maxH * 2.5);
      ctx.clip();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = alpha * (0.5 + this.amp * 0.5);
      ctx.fill(path);
      ctx.restore();
      var px = x0 + innerW * frac;
      ctx.globalAlpha = alpha * 0.9;
      ctx.fillStyle = rgba(this.beamB, 0.9);
      ctx.fillRect(px - 0.5, cy - maxH * 1.05, 1, maxH * 2.1);
    }
    ctx.restore();
  };

  Downconverter.prototype.drawArtifact = function (alpha, T) {
    if (this.mode !== 'waveform') { return; }
    if (this.vis === 'orbit') { this.drawOrbit(alpha, T); }
    else if (this.vis === 'scope') { this.drawScope(alpha, T); }
    else { this.drawWave(alpha, T); }
  };

  /* Orbital ring: the peaks wrapped around a dial, radar-hand playhead. */
  Downconverter.prototype.drawOrbit = function (alpha, T) {
    if (alpha <= 0.01) { return; }
    var ctx = this.ctx;
    var peaks = this.currentPeaks();
    var n = Math.min(peaks.length, 96);
    var g = this.orbitGeom();
    var pulse = 1 + this.amp * 0.25;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.lineWidth = Math.max(1.5, (Math.PI * 2 * g.r0) / n * 0.45);
    for (var i = 0; i < n; i++) {
      var a = 0.08 + 0.92 * clamp(+peaks[i] || 0, 0, 1);
      var ang = (i / n) * Math.PI * 2 - Math.PI / 2;
      var c = mixRgb(this.beamA, this.beamB, i / n);
      ctx.strokeStyle = rgba(c, 0.55);
      ctx.beginPath();
      ctx.moveTo(g.cx + Math.cos(ang) * g.r0, g.cy + Math.sin(ang) * g.r0);
      ctx.lineTo(g.cx + Math.cos(ang) * (g.r0 + a * g.r1 * pulse),
                 g.cy + Math.sin(ang) * (g.r0 + a * g.r1 * pulse));
      ctx.stroke();
    }
    ctx.strokeStyle = rgba(this.beamA, 0.5);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(g.cx, g.cy, g.r0 * 0.55, 0, Math.PI * 2);
    ctx.stroke();
    if (this.audio && isFinite(this.audio.duration) && this.audio.duration > 0 &&
        this.audio.currentTime > 0) {
      var frac = clamp(this.audio.currentTime / this.audio.duration, 0, 1);
      var ha = frac * Math.PI * 2 - Math.PI / 2;
      var hr = g.r0 + g.r1 * pulse;
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = rgba(this.beamB, 0.9);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(g.cx + Math.cos(ha) * g.r0 * 0.55, g.cy + Math.sin(ha) * g.r0 * 0.55);
      ctx.lineTo(g.cx + Math.cos(ha) * hr, g.cy + Math.sin(ha) * hr);
      ctx.stroke();
      var bloom = this.sprites[SPRITES - 1];
      ctx.drawImage(bloom, g.cx + Math.cos(ha) * hr - 8, g.cy + Math.sin(ha) * hr - 8, 16, 16);
    }
    ctx.restore();
  };

  /* Rotate through a small palette so the scope colour shifts as the track
     plays. Faster rotation when audible, calm rotation at rest. */
  Downconverter.prototype.scopePaletteAt = function (t) {
    var pal = this.scopePalette || (this.scopePalette = [
      this.beamA, this.beamB, [236, 200, 121] /* gold */
    ]);
    var n = pal.length;
    var f = ((t % 1) + 1) % 1;
    var i = Math.floor(f * n);
    var next = (i + 1) % n;
    var local = f * n - i;
    return mixRgb(pal[i], pal[next], local);
  };

  /* Oscilloscope: the live signal while audible; the peak envelope at rest.
     Colour rotates through the FARA palette continuously — accelerated by
     playback + amp so the wave "warms" the more it plays. */
  Downconverter.prototype.drawScope = function (alpha, T) {
    if (alpha <= 0.01) { return; }
    var ctx = this.ctx;
    var x0 = this.w * 0.08;
    var innerW = this.w * 0.84;
    var cy = this.h * 0.52;
    var maxH = this.h * 0.34;
    var phaseSpeed = this.playing ? (0.09 + this.amp * 0.10) : 0.045;
    var phase = T * phaseSpeed;
    var c1 = this.scopePaletteAt(phase);
    var c2 = this.scopePaletteAt(phase + 0.5);
    var grad = ctx.createLinearGradient(x0, 0, x0 + innerW, 0);
    grad.addColorStop(0, rgba(c1, 0.9));
    grad.addColorStop(1, rgba(c2, 0.9));
    var i, x, y;
    ctx.save();
    var live = this.playing && this.analyser && this.timeData;
    ctx.beginPath();
    if (live) {
      var N = this.timeData.length;
      for (i = 0; i < N; i += 2) {
        x = x0 + (i / (N - 1)) * innerW;
        y = cy + ((this.timeData[i] - 128) / 128) * maxH;
        if (i === 0) { ctx.moveTo(x, y); } else { ctx.lineTo(x, y); }
      }
    } else {
      var peaks = this.currentPeaks();
      var n = Math.min(peaks.length, 96);
      for (i = 0; i < n; i++) {
        x = x0 + (i / (n - 1)) * innerW;
        var sgn = (i % 2 === 0 ? 1 : -1);
        y = cy + sgn * (0.05 + clamp(+peaks[i] || 0, 0, 1) * 0.35) * maxH
          + Math.sin(T * 2 + i * 0.7) * 1.5;
        if (i === 0) { ctx.moveTo(x, y); } else { ctx.lineTo(x, y); }
      }
    }
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = alpha * 0.35;
    ctx.strokeStyle = grad;
    ctx.lineWidth = 5;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 1.6;
    ctx.stroke();
    ctx.globalAlpha = alpha * 0.3;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x0, cy);
    ctx.lineTo(x0 + innerW, cy);
    ctx.stroke();
    ctx.restore();
  };

  Downconverter.prototype.draw = function (T) {
    var ctx = this.ctx;
    this.readAmp();
    ctx.clearRect(0, 0, this.w, this.h);

    var scrubT = this.value() / 1000;
    var eAll = easeCubic(scrubT);
    var drift = this.cloudR * 0.06;
    var glow = 0.72 + this.amp * 0.28;      /* brightness, clamped via amp */
    var sizeMod = 1 + this.amp * 0.3;

    /* 1 · Nebula bloom breathing behind whatever is still spatial */
    if (eAll < 0.99 && this.nebula) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = (1 - eAll) * (0.8 + 0.2 * Math.sin(T * 0.6));
      ctx.fillStyle = this.nebula;
      ctx.fillRect(this.cloudCX - this.nebulaR, this.cloudCY - this.nebulaR,
        this.nebulaR * 2, this.nebulaR * 2);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    }

    /* 2 · The flat artifact assembling underneath the particles */
    this.drawArtifact(eAll, T);

    /* 3 · The particle field, twinkling while spatial, crisp once flat */
    ctx.globalCompositeOperation = 'lighter';
    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i];
      var local = p.o >= 1 ? 1 : clamp((scrubT - p.o) / (1 - p.o), 0, 1);
      var e = easeCubic(local);
      var cx = this.cloudCX + p.u * this.cloudR
        + Math.sin(T * p.sp + p.ph1) * drift
        + Math.sin(T * 6.3 + p.ph2 * 13) * 0.7;   /* tiny jitter */
      var cy = this.cloudCY + p.v * this.cloudR
        + Math.cos(T * p.sp * 0.8 + p.ph2) * drift * 0.8
        + Math.cos(T * 5.7 + p.ph1 * 11) * 0.7;
      var x = cx + (p.tx - cx) * e;
      var y = cy + (p.ty - cy) * e;
      /* Resolved = crisper: shrink the glow as the particle flattens. */
      var r = (2.1 + p.s * 2.6) * (1 - 0.32 * e) * sizeMod;
      ctx.globalAlpha = glow * (e < 1 ? 0.78 + 0.22 * Math.sin(T * 1.9 + p.ph2 * 9) : 1);
      ctx.drawImage(this.sprites[p.ci], x - r, y - r, r * 2, r * 2);
    }

    /* 4 · Mid-scrub: the downconversion ray from cloud to artifact */
    if (scrubT > 0.03 && scrubT < 0.97) {
      var bx = this.w * 0.08 + this.w * 0.84 * scrubT;
      var by = this.h * 0.52;
      var beam = ctx.createLinearGradient(this.cloudCX, 0, bx, 0);
      beam.addColorStop(0, rgba(this.beamA, 0));
      beam.addColorStop(0.7, rgba(this.beamA, 0.35));
      beam.addColorStop(1, rgba(this.beamB, 0.8));
      ctx.globalAlpha = 0.6;
      ctx.fillStyle = beam;
      ctx.fillRect(this.cloudCX, by - 0.75, Math.max(0, bx - this.cloudCX), 1.5);
      var bloom = this.sprites[SPRITES - 1];
      ctx.globalAlpha = 0.85;
      ctx.drawImage(bloom, bx - 7, by - 7, 14, 14);
    }

    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    try { this.el.style.setProperty('--sx-amp', this.amp.toFixed(3)); } catch (e) {}
  };

  /* Reduced-motion / static path: the single resolved flat state. */
  Downconverter.prototype.drawStatic = function () {
    var ctx = this.ctx;
    ctx.clearRect(0, 0, this.w, this.h);
    this.drawArtifact(1, 0);
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = 0.8;
    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i];
      var r = (2.1 + p.s * 2.6) * 0.68;
      ctx.drawImage(this.sprites[p.ci], p.tx - r, p.ty - r, r * 2, r * 2);
    }
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  };

  Downconverter.prototype.destroy = function () {
    this.sleep();
    if (this.io) { this.io.disconnect(); }
    if (this.ro) { this.ro.disconnect(); }
    if (this.aborter) { this.aborter.abort(); }
    if (this.audio) { this.audio.pause(); this.audio.src = ''; }
    if (this.actx && this.actx.close) { this.actx.close().catch(function () {}); }
    this.el.__sxdc = null;
  };

  /* -------------------------------------------------------------- boot --- */

  function init(el) {
    if (!el || !el.querySelector) { return null; }
    if (el.__sxdc) { el.__sxdc.destroy(); }
    var inst = new Downconverter(el);
    if (!inst.canvas) { return null; }
    el.__sxdc = inst;
    /* First paint happens on intersection via the instance's own observer;
       reduced mode paints its static flat state as soon as it is visible. */
    return inst;
  }

  function boot() {
    var els = document.querySelectorAll('.sx-downconverter');
    if (!els.length) { return; }
    if (!('IntersectionObserver' in window)) {
      /* Ancient fallback: init immediately (still no loop until visible). */
      for (var i = 0; i < els.length; i++) { init(els[i]); }
      return;
    }
    var lazy = new IntersectionObserver(function (entries, obs) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting && !entries[i].target.__sxdc) {
          init(entries[i].target);
          obs.unobserve(entries[i].target);
        }
      }
    }, { rootMargin: '160px' });
    for (var j = 0; j < els.length; j++) { lazy.observe(els[j]); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.SXDownconverter = { init: init };
}());

